// The specific reasons this module's backend gives, in the four languages.
//
// Two codes travel INSIDE an error and are not the code it is thrown with:
//
//   · `reasons` — what a generic refusal carries in `details.reason` or
//     `details.code` (`ConflictError(…, 'CONFLICT', { reason: 'ALREADY_CLOSED' })`).
//     errorMessage() says it from `errors.reasons.<REASON>` instead of the
//     generic «that record is no longer in the state you expected».
//   · `fields` — the code of each field of a ValidationError, said by
//     fieldErrors() from `errors.fields.<CODE>` instead of «not a valid value».
//
// Generic field codes (EMPTY, IN_THE_FUTURE, NOT_FOUND…) are the shell's and
// are not repeated here. platform/web/tests/errorReasons.test.js reads the
// backend and fails on a code without its four sentences, and on two
// modules saying one code differently (the shell merges them into one).

const errorReasons = {
  es: {
    reasons: {
      ALREADY_CLOSED: 'Este expediente ya está cerrado',
      ALREADY_FILED: 'Esta persona ya tiene expediente',
      ALREADY_OPEN: 'Este expediente está abierto',
      ALREADY_VOID: 'Esa versión ya está anulada',
      DUPLICATE_CODE: 'Ya hay un tipo de documento con ese código',
      NOTHING_FILED: 'No hay nada presentado que revisar',
      NOT_ELIGIBLE: 'Esta persona no cumple los requisitos de ese trabajo',
      OVERRIDE_REQUIRED: 'No cumple los requisitos: para asignarla hay que justificarlo',
      TYPE_ARCHIVED: 'Ese tipo de documento está archivado',
      TYPE_IN_USE: 'Hay requisitos en uso que piden ese tipo de documento',
    },
  },
  ca: {
    reasons: {
      ALREADY_CLOSED: 'Aquest expedient ja està tancat',
      ALREADY_FILED: 'Aquesta persona ja té expedient',
      ALREADY_OPEN: 'Aquest expedient és obert',
      ALREADY_VOID: 'Aquesta versió ja està anul·lada',
      DUPLICATE_CODE: 'Ja hi ha un tipus de document amb aquest codi',
      NOTHING_FILED: 'No hi ha res presentat per revisar',
      NOT_ELIGIBLE: 'Aquesta persona no compleix els requisits d’aquesta feina',
      OVERRIDE_REQUIRED: 'No compleix els requisits: per assignar-la cal justificar-ho',
      TYPE_ARCHIVED: 'Aquest tipus de document està arxivat',
      TYPE_IN_USE: 'Hi ha requisits en ús que demanen aquest tipus de document',
    },
  },
  en: {
    reasons: {
      ALREADY_CLOSED: 'This file is already closed',
      ALREADY_FILED: 'This person already has a file',
      ALREADY_OPEN: 'This file is open',
      ALREADY_VOID: 'That version is already void',
      DUPLICATE_CODE: 'There is already a document type with that code',
      NOTHING_FILED: 'There is nothing filed to review',
      NOT_ELIGIBLE: 'This person does not meet the requirements of that work',
      OVERRIDE_REQUIRED: 'The requirements are not met: assigning them needs a justification',
      TYPE_ARCHIVED: 'That document type is archived',
      TYPE_IN_USE: 'Requirements in use still ask for that document type',
    },
  },
  fr: {
    reasons: {
      ALREADY_CLOSED: 'Ce dossier est déjà clôturé',
      ALREADY_FILED: 'Cette personne a déjà un dossier',
      ALREADY_OPEN: 'Ce dossier est ouvert',
      ALREADY_VOID: 'Cette version est déjà annulée',
      DUPLICATE_CODE: 'Il existe déjà un type de document avec ce code',
      NOTHING_FILED: 'Il n’y a rien de déposé à examiner',
      NOT_ELIGIBLE: 'Cette personne ne remplit pas les conditions de ce travail',
      OVERRIDE_REQUIRED: 'Les conditions ne sont pas remplies : l’affecter exige une justification',
      TYPE_ARCHIVED: 'Ce type de document est archivé',
      TYPE_IN_USE: 'Des exigences en vigueur demandent encore ce type de document',
    },
  },
}

/** The module's catalogue with these hung from `errors`, locale by locale. */
export function withErrorReasons(base) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    const own = errorReasons[locale] ?? {}
    merged[locale] = {
      ...catalogue,
      errors: {
        ...catalogue.errors,
        reasons: { ...catalogue.errors?.reasons, ...own.reasons },
        fields: { ...catalogue.errors?.fields, ...own.fields },
      },
    }
  }
  return merged
}

export default errorReasons
